import {
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IconArrowUp, IconFileText, IconImage, IconStop, IconX } from '@/components/ui/icons';
import { formatFileSize } from '@/lib/chat-attachment-rules';
import type { AttachmentDraft } from '@/lib/chat-attachments';
import type { ChatView } from '@/lib/chat-view';
import { useCoarsePointer } from '@/lib/use-media';
import { cn } from '@/lib/utils';

/** Files of a paste/drop. Everything is passed on: `attachments.add` refuses what is not a valid image, with a reason. */
const filesOf = (list: DataTransferItemList | FileList | null | undefined): File[] => {
  if (!list) return [];
  const out: File[] = [];
  for (const it of Array.from(list as ArrayLike<DataTransferItem | File>)) {
    const f = it instanceof File ? it : it.kind === 'file' ? it.getAsFile() : null;
    if (f) out.push(f);
  }
  return out;
};
const hasFiles = (e: DragEvent | globalThis.DragEvent) =>
  Array.from(e.dataTransfer?.types ?? []).includes('Files');

/** Screenshots waiting to be sent: thumbnails with a remove button. */
function DraftStrip({ attachments }: { attachments: AttachmentDraft }) {
  if (!attachments.items.length && !attachments.preparing) return null;
  return (
    <ul aria-label="Attached files" className="flex flex-wrap gap-2 px-3 pt-2.5">
      {attachments.items.map((d) => (
        <li key={d.key} className="group relative">
          {d.url ? (
            <img
              src={d.url}
              alt={d.name}
              className="h-16 w-16 rounded-lg border border-border object-cover"
            />
          ) : (
            <div
              title={d.name}
              className="flex h-16 max-w-[200px] items-center gap-2 rounded-lg border border-border bg-panel px-2.5 text-[13px] text-fg"
            >
              <IconFileText size={16} />
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{d.name}</span>
                <span className="text-xs text-muted">{formatFileSize(d.sizeBytes)}</span>
              </span>
            </div>
          )}
          <button
            type="button"
            aria-label={`Remove ${d.name}`}
            onClick={() => attachments.remove(d.key)}
            className="absolute -top-1.5 -right-1.5 inline-flex size-5 touch:size-7 items-center justify-center rounded-md border border-border bg-panel text-muted shadow-soft hover:text-fg"
          >
            <IconX size={10} />
          </button>
        </li>
      ))}
      {attachments.preparing ? (
        <li className="flex h-16 w-16 animate-pulse items-center justify-center rounded-lg border border-dashed border-border-strong text-faint">
          <IconImage size={16} />
        </li>
      ) : null}
    </ul>
  );
}

/**
 * Message box shared by the chat panes: a separate card with its own backdrop (so it never reads as part of the
 * message list), an auto-growing textarea, a footer slot (e.g. model picker) and a send button that becomes Stop
 * while a run is active. With `attachments`, screenshots can be added by the image button, paste or drag & drop.
 */
export function Composer({
  view,
  value,
  onChange,
  onSubmit,
  onKeyDown,
  placeholder,
  ariaLabel,
  inputRef,
  disabled,
  canSend = true,
  busy,
  onStop,
  above,
  footer,
  controls,
  attachments,
  bare,
}: {
  view: ChatView;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  ariaLabel: string;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  disabled?: boolean;
  canSend?: boolean;
  busy?: boolean;
  onStop?: () => void;
  /** Rendered just above the card (suggestion popup). */
  above?: ReactNode;
  footer?: ReactNode;
  /** Compact settings in the composer toolbar. */
  controls?: ReactNode;
  attachments?: AttachmentDraft;
  /** No backdrop strip (the composer sits on a page, e.g. the new-chat screen). */
  bare?: boolean;
}) {
  const advanced = view === 'advanced';
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const coarse = useCoarsePointer();
  const hasContent = !!value.trim() || !!attachments?.items.length;
  // Typing while the agent works queues the message: the button turns from Stop into Send.
  const stopping = !!busy && !!onStop && !hasContent;

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!attachments) return;
    const files = filesOf(e.clipboardData?.items);
    if (!files.length) return;
    // A pasted screenshot is attached; pasted text (if any) still goes into the box.
    if (!e.clipboardData.getData('text/plain')) e.preventDefault();
    attachments.add(files);
  };
  // A file dropped next to the message box must not make the browser navigate to it (and lose the draft).
  const canAttach = !!attachments;
  useEffect(() => {
    if (!canAttach) return;
    const stop = (e: globalThis.DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragover', stop);
      window.removeEventListener('drop', stop);
    };
  }, [canAttach]);
  const dropProps = attachments
    ? {
        onDragOver: (e: DragEvent) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setDragging(true);
        },
        onDragLeave: (e: DragEvent) => {
          // Moving over a child (textarea, buttons) also fires leave on the card: only reset when really leaving.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
        },
        onDrop: (e: DragEvent) => {
          setDragging(false);
          if (!hasFiles(e)) return;
          e.preventDefault();
          attachments.add(filesOf(e.dataTransfer.files));
        },
      }
    : {};

  return (
    <div
      className={cn(
        'relative shrink-0',
        !bare && 'border-t border-border bg-panel/40 px-3 pt-2.5 pb-3',
        advanced ? 'font-mono' : 'font-sans',
      )}
    >
      {above}
      <div
        {...dropProps}
        className={cn(
          'rounded-2xl border border-border-strong bg-panel shadow-soft transition-colors',
          'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25',
          dragging && 'border-accent bg-accent/5 ring-2 ring-accent/25',
        )}
      >
        {dragging ? (
          <div
            aria-hidden
            className="pointer-events-none flex items-center justify-center gap-2 px-3.5 pt-3 text-[13px] font-medium text-accent"
          >
            <IconImage size={15} /> Drop files to attach them
          </div>
        ) : null}
        {attachments ? <DraftStrip attachments={attachments} /> : null}
        <div className="flex items-start gap-2 px-3.5 pt-2.5">
          {advanced ? (
            <span aria-hidden className="pt-px text-[13px] text-muted select-none">
              &gt;
            </span>
          ) : null}
          <textarea
            ref={inputRef}
            aria-label={ariaLabel}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={onPaste}
            onKeyDown={(e) => {
              onKeyDown?.(e);
              if (e.defaultPrevented) return;
              // Phone keyboards have no Shift: Enter is a newline there and the send button sends.
              if (e.key === 'Enter' && !e.shiftKey && !coarse && !e.nativeEvent.isComposing) {
                e.preventDefault();
                onSubmit();
              }
            }}
            rows={Math.min(8, Math.max(1, value.split('\n').length))}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'min-h-0 w-full resize-none bg-transparent leading-relaxed text-fg placeholder:text-faint focus:outline-none',
              advanced ? 'text-[13px]' : 'text-[14px]',
              'touch:text-[16px]',
            )}
          />
        </div>
        <div className="flex items-center gap-2 px-2.5 pt-1 pb-2">
          {attachments ? (
            <>
              <button
                type="button"
                aria-label="Attach a screenshot or log file"
                title="Attach a screenshot or a .ndjson / .log / .txt / .json file (or paste / drop it)"
                onClick={() => fileRef.current?.click()}
                disabled={disabled}
                className="inline-flex size-7 touch:size-10 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-hover hover:text-fg disabled:opacity-40"
              >
                <IconImage size={15} />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,.ndjson,.log,.txt,.json"
                multiple
                hidden
                onChange={(e) => {
                  attachments.add(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
            </>
          ) : null}
          {controls}
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-xs text-muted">
            {footer}
          </div>
          {stopping ? (
            <button
              type="button"
              onClick={onStop}
              aria-label="Stop"
              className="inline-flex size-7 touch:size-10 shrink-0 items-center justify-center rounded-lg border border-danger/40 bg-danger/10 text-danger transition-colors hover:bg-danger/20"
            >
              <IconStop size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              aria-label="Send"
              title="Enter to send · Shift+Enter for a new line"
              disabled={disabled || !canSend || !hasContent || attachments?.preparing}
              className="inline-flex size-7 touch:size-10 shrink-0 items-center justify-center rounded-lg bg-cta text-cta-fg transition-colors hover:bg-cta-hover disabled:pointer-events-none disabled:opacity-35"
            >
              <IconArrowUp size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
